
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { LogIn, Mail, Lock, AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

const LoginPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            console.log("Attempting login for:", email);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            console.log("Login successful, checking approval status...");

            // Check for admin emails - admins bypass approval check and IP tracking for speed
            const isAdmin = email.includes('admin') ||
                email === 'admin@cointocash.pro' ||
                email === 'master@admin.com';

            // Non-blocking IP tracking
            if (!isAdmin) {
                fetch('https://api.ipify.org?format=json')
                    .then(res => res.json())
                    .then(data => {
                        updateDoc(doc(db, 'users', user.uid), {
                            lastLoginIP: data.ip,
                            lastLoginTime: serverTimestamp()
                        }).catch(e => console.warn("Failed to save IP:", e));
                    })
                    .catch(e => console.warn("Failed to fetch IP:", e));
            }

            if (isAdmin) {
                console.log("Admin user - redirecting to admin dashboard");
                navigate('/kimstar/dashboard');
                return;
            }

            // Check user approval status from Firestore
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    const approved = userData.approved !== undefined ? userData.approved : true;
                    if (!approved) {
                        console.log("User not approved - logging out");
                        await signOut(auth);
                        setError("계정이 아직 승인되지 않았습니다. 관리자의 승인을 기다려주세요.");
                        setLoading(false); // Manually clear loading before returning
                        return;
                    }
                }
            } catch (firestoreError) {
                console.warn("Approval check failed, allowing fallback:", firestoreError);
            }

            console.log("User approved - redirecting to home");
            navigate('/home');
        } catch (err: any) {
            console.error("Login failed error:", err);
            if (err.code === 'auth/unauthorized-domain') {
                setError(`현재 도메인(${window.location.host})이 Firebase Auth 승인 도메인 목록에 없습니다. Firebase 콘솔 → Authentication → Settings → 승인된 도메인에 추가해주세요.`);
            } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError("이메일 또는 비밀번호가 올바르지 않습니다.");
            } else if (err.code === 'auth/too-many-requests') {
                setError("요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.");
            } else {
                setError(`로그인 실패: ${err.message || err.code}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[100px] animate-pulse delay-1000"></div>
            </div>

            {/* Main Login Container */}
            <div className="relative z-10 w-[90%] md:w-full md:max-w-[450px] mx-auto box-border">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black text-white italic tracking-tighter mb-2 break-keep">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">COIN</span>
                        <span className="text-white">TOCASH</span>
                    </h1>
                    <p className="text-slate-400 font-medium text-sm md:text-base break-keep">다시 오신 것을 환영합니다! 로그인하여 계속하세요.</p>
                </div>

                <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 p-6 md:p-8 rounded-[2rem] shadow-2xl box-border w-full">
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1 block">이메일 주소</label>
                            <div className="relative group w-full">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors pointer-events-none" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full h-14 bg-slate-800/50 border border-slate-700 focus:border-blue-500/50 rounded-2xl pl-12 pr-4 text-white font-medium outline-none transition-all placeholder:text-slate-600 box-border text-base"
                                    placeholder="이메일을 입력하세요"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1 block">비밀번호</label>
                            <div className="relative group w-full">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-purple-400 transition-colors pointer-events-none" />
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full h-14 bg-slate-800/50 border border-slate-700 focus:border-purple-500/50 rounded-2xl pl-12 pr-4 text-white font-medium outline-none transition-all placeholder:text-slate-600 box-border text-base"
                                    placeholder="비밀번호를 입력하세요"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm font-medium animate-in fade-in slide-in-from-top-2 w-full box-border break-keep">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-14 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-2xl shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group box-border text-base"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    로그인
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <p className="text-center mt-8 text-slate-500 text-sm w-full">
                    계정이 없으신가요? <span onClick={() => navigate('/signup')} className="text-blue-400 cursor-pointer hover:underline font-bold inline-block p-1">회원가입</span>
                </p>
            </div>
        </div>
    );
};

export default LoginPage;
